import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import Skeleton from "react-loading-skeleton";

import { STRIPE_PUBLIC_KEY } from "../../general/config";
import { checkoutLoading, sessionKey, paymentPageTokenSelector } from "../../redux/selectors/pagesSelectors";
import { useHeaderSlot } from "../../context/HeaderSlotContext";
import RegisterBlock from "../../components/Registraion/RegisterBlock";
import CheckoutForm from "../../components/CheckoutForm";
import { StripeInfoCard } from "../../components/Payment/StripeInfoCard";

import "./additionalPackage.scss";
import { PAYMENT_PAYPAL_ENABLED } from "../../constants/constants";
import { ReactComponent as PayPalLogo } from "../../svg/filled/paypal.svg";
import { redirectToPayment } from "../../redux/actions/checkout";
import { getCurrentPaymentUiMode, shouldUseEmbeddedCheckout } from "../../lib/paymentUiMode";

// RTK Query hooks
import {
    useGetProductTariffsQuery,
    useBillPackagePayQuery,
    useBillCheckoutMutation,
} from "../../redux/services/baseApi";

export default function AdditionalPackagePage() {
    const dispatch = useDispatch();
    const location = useLocation();

    const token = useSelector(sessionKey);
    const payment_page_token = useSelector(paymentPageTokenSelector);
    const checkoutIsLoading = useSelector(checkoutLoading);
    const { setHeaderContent } = useHeaderSlot();

    // Stripe
    const stripePromise = useMemo(() => loadStripe(STRIPE_PUBLIC_KEY), []);
    const [stripe, setStripe] = useState(null);

    // product_id из URL
    const productId = useMemo(() => {
        const sp = new URLSearchParams(location.search);
        const id = sp.get("product_id");
        return id ? Number(id) : null;
    }, [location.search]);

    const uiMode = useMemo(() => getCurrentPaymentUiMode(), [location.search]);
    const useEmbeddedCheckout = shouldUseEmbeddedCheckout(uiMode);

    // Header slot
    useEffect(() => {
        setHeaderContent(<RegisterBlock/>);
        return () => setHeaderContent(null);
    }, [setHeaderContent]);

    useEffect(() => {
        let alive = true;
        stripePromise.then((s) => {
            if (!alive) return;
            setStripe(s);
        });
        return () => {
            alive = false;
        };
    }, [stripePromise]);

    // Products
    const productsQuery = useGetProductTariffsQuery();
    const products = productsQuery.data ?? {};
    const productsBusy = productsQuery.isLoading || productsQuery.isFetching;

    // bill_package_pay
    const packageQuery = useBillPackagePayQuery(
        { productId, token, paymentPageToken: payment_page_token },
        { skip: productId == null }
    );

    const packageResp = packageQuery.data;
    const packageBusy = packageQuery.isLoading || packageQuery.isFetching;

    const errorNo = packageQuery.error?.data?.error_no;
    const errorMessage = packageQuery.error?.data?.error_message ?? "";

    const packageData = packageResp?.data ?? null;

    const isBlocked = [403, 409].includes(errorNo);
    const isSuccess = packageData?.operation_status === "SUCCESS";
    const errorResponse = !isSuccess;

    const comment = useMemo(() => {
        if (productId == null) return "Missing product_id.";
        if (isBlocked) return errorMessage || "Access denied.";
        if (packageQuery.isError) return "Network error.";
        if (isSuccess) return packageData?.comment ?? "";
        return "This link is incorrect.";
    }, [productId, isBlocked, errorMessage, packageQuery.isError, isSuccess, packageData]);

    //bill_checkout
    const [clientSecret, setClientSecret] = useState(null);
    const [billCheckout, checkoutResult] = useBillCheckoutMutation();

    useEffect(() => {
        setClientSecret(null);
    }, [productId, token, payment_page_token]);

    useEffect(() => {
        if (productId == null) return;
        if (!isSuccess) return;
        if (!useEmbeddedCheckout) return;
        if (clientSecret) return;

        billCheckout({
            productId,
            token,
            paymentPageToken: payment_page_token,
            currency: "USD",
        })
            .unwrap()
            .then((resp) => {
                if (resp?.data?.operation_status === "SUCCESS") {
                    setClientSecret(resp?.data?.client_secret ?? null);
                }
            })
            .catch(() => {
            });
    }, [productId, isSuccess, useEmbeddedCheckout, clientSecret, billCheckout, token, payment_page_token]);

    // Description of the package from products
    const extraPackageDescription = useMemo(() => {
        if (productId == null) return [];
        const product =
            Object.values(products ?? {}).find((p) => Number(p?.product_id) === Number(productId)) ?? null;
        return product?.extra_package_description_items ?? [];
    }, [products, productId]);

    const pageLoading = productId != null ? packageBusy : productsBusy;

    const handleStripe = () => {
        if (productId == null || checkoutIsLoading) return;

        dispatch(redirectToPayment("stripe", {
            method_name: "bill_checkout",
            product_id: productId,
            payment_page_token: payment_page_token,
            currency: "USD",
            extra_package_only: 1,
            cancel_url: window.location.href,
        }));
    };

    const handlePayPal = () => {
        if (productId == null || checkoutIsLoading) return;

        const postData = {
            method_name: "bill_checkout",
            product_id: productId,
            payment_page_token: payment_page_token,
            currency: "USD",
            extra_package_only: 1,
            cancel_url: window.location.href,
        };

        dispatch(redirectToPayment("paypal", postData));
    };

    if (pageLoading) {
        return (
            <div className="additional-package">
                <Skeleton height={230} width={680} style={{ marginBottom: 10 }}/>
            </div>
        );
    }

    return (
        <>
            <div className="additional-package">
                <div className="additional-package__card">
                    <h2 className="additional-package__title">Additional Package</h2>
                    <p className="additional-package__comment">{comment}</p>

                    {!errorResponse && (
                        <ul className="text--left">
                            {extraPackageDescription?.map((item, index) => (
                                <li key={`${index}_additional-package`}>{item}</li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {!errorResponse && (
                <div className="additional-package__checkout-loading">
                    {useEmbeddedCheckout && checkoutResult.isLoading && <Skeleton height={600} width={900} style={{ marginTop: 20 }}/>}

                    {useEmbeddedCheckout && clientSecret && !checkoutResult.isLoading && (
                        <CheckoutForm stripe={stripe} pagesClientSecret={clientSecret}/>
                    )}

                    {!useEmbeddedCheckout && (
                        <StripeInfoCard
                            activeBox
                            checkoutTitleStripe="Make a one time credit/debit card payment"
                            onClick={handleStripe}
                            loading={checkoutIsLoading}
                        />
                    )}

                    <div className="card__or">OR</div>

                    <div className={`card__list card__list${PAYMENT_PAYPAL_ENABLED ? "" : "--single"}`}>
                        {!!PAYMENT_PAYPAL_ENABLED && (
                            <div className="card__container">
                                <div>
                                    <h3 className="card__title">Make a one time PayPal payment</h3>
                                    <span className="card__description">
                                  It may take up to 5 minutes for the payment process
                                </span>
                                </div>

                                <div className="card__item">
                                    <PayPalLogo className="card__logo"/>
                                </div>

                                <div className="card__item">
                                    <button
                                        disabled={checkoutResult.isLoading || checkoutIsLoading}
                                        onClick={handlePayPal}
                                        type="button"
                                        className={`button card__button button--proceed ${(checkoutResult.isLoading || checkoutIsLoading) ? "disabled" : ""}`.trim()}
                                    >
                                        <span className="text-2 bold">Pay Now</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
